import React, { useEffect, useState } from 'react';
import {
  Paper, Typography, Button, Table, TableHead, TableRow,
  TableCell, TableBody, TableContainer, Modal, Box, TextField, IconButton,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  Alert, Tooltip, MenuItem, CircularProgress, Card, useMediaQuery, useTheme
} from '@mui/material';
import { Add, Edit, Delete, Close } from '@mui/icons-material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import HistoryIcon from '@mui/icons-material/History';
import { useApi } from '../api';
import { motion } from 'framer-motion';
import { obtenerImagenAutenticada, formatearPrecio } from './utils';

const PLACA_REGEX = /^[A-Z]{3}[0-9]{3}$/;


const modalStyle = {
  position: 'absolute',
  top: '50%', left: '50%',
  transform: 'translate(-50%, -50%)',
  backgroundColor: 'white',
  padding: 4,
  borderRadius: 2,
  boxShadow: 24,
  width: 400,
};

const Vehiculos = () => {
  const [vehiculos, setVehiculos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [nuevoVehiculo, setNuevoVehiculo] = useState({
    placa: '', marca: '', linea: '', modelo: '', color: '', tarjeta_propiedad: null, cliente_id: ''
  });
  const [errores, setErrores] = useState({});
  const [modoEdicion, setModoEdicion] = useState(false);
  const [vehiculoActual, setVehiculoActual] = useState(null);
  const [imagenSeleccionada, setImagenSeleccionada] = useState(null);
  const [vehiculoAEliminar, setVehiculoAEliminar] = useState(null);
  const [dialogoAbierto, setDialogoAbierto] = useState(false);
  const [errorMensaje, setErrorMensaje] = useState('');
  const [historialAbierto, setHistorialAbierto] = useState(false);
  const [vehiculoHistorial, setVehiculoHistorial] = useState(null);
  const [reparacionesHistorial, setReparacionesHistorial] = useState([]);
  const [cargandoHistorial, setCargandoHistorial] = useState(false);

  const [busqueda, setBusqueda] = useState('');
  const api = useApi();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const vehiculosFiltrados = vehiculos.filter((v) => {
    const termino = busqueda.trim().toLowerCase();
    if (!termino) return true;
    return [v.placa, v.marca, v.linea, v.modelo, v.color, v.cliente?.nombre]
      .some((campo) => (campo || '').toLowerCase().includes(termino));
  });

  const fetchVehiculos = async () => {
    try {
      const response = await api.get(`${process.env.REACT_APP_API_URL}/api/vehiculos/`);
      setVehiculos(response.data);
    } catch (err) {
      console.error('Error al cargar vehículos', err);
    }
  };

  const fetchClientes = async () => {
    try {
      const response = await api.get(`${process.env.REACT_APP_API_URL}/api/clientes/`);
      setClientes(response.data);
    } catch (err) {
      console.error('Error al cargar clientes', err);
    }
  };

  useEffect(() => {
    fetchVehiculos();
    fetchClientes();
  }, []);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    const valorFinal = name === 'placa' ? value.toUpperCase() : value;
    setNuevoVehiculo({ ...nuevoVehiculo, [name]: files ? files[0] : valorFinal });
  };

  const validarFormulario = () => {
    const nuevosErrores = {};
    if (!nuevoVehiculo.placa) {
      nuevosErrores.placa = 'La placa es obligatoria';
    } else if (!PLACA_REGEX.test(nuevoVehiculo.placa)) {
      nuevosErrores.placa = 'Formato inválido. Debe ser 3 letras y 3 números, ej: BTL847';
    }
    if (!nuevoVehiculo.marca) nuevosErrores.marca = 'La marca es obligatoria';
    if (!nuevoVehiculo.linea) nuevosErrores.linea = 'La línea es obligatoria';
    if (!nuevoVehiculo.modelo) nuevosErrores.modelo = 'El modelo es obligatorio';
    if (!nuevoVehiculo.color) nuevosErrores.color = 'El color es obligatorio';
    setErrores(nuevosErrores);
    return Object.keys(nuevosErrores).length === 0;
  };

  const abrirModal = (vehiculo = null) => {
    if (vehiculo) {
      setModoEdicion(true);
      setVehiculoActual(vehiculo);
      setNuevoVehiculo({
        placa: vehiculo.placa,
        marca: vehiculo.marca,
        linea: vehiculo.linea,
        modelo: vehiculo.modelo,
        color: vehiculo.color,
        tarjeta_propiedad: null,
        cliente_id: vehiculo.cliente ? vehiculo.cliente.id : ''
      });
    } else {
      setModoEdicion(false);
      setVehiculoActual(null);
      setNuevoVehiculo({
        placa: '', marca: '', linea: '', modelo: '', color: '', tarjeta_propiedad: null, cliente_id: ''
      });
    }
    setErrores({});
    setErrorMensaje('');
    setModalOpen(true);
  };

  const cerrarModal = () => setModalOpen(false);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!validarFormulario()) return;

    try {
      const formData = new FormData();
      for (const key in nuevoVehiculo) {
        if (nuevoVehiculo[key]) formData.append(key, nuevoVehiculo[key]);
      }
      // Se envía explícito (incluso vacío) para poder desasignar un cliente ya asociado
      formData.set('cliente_id', nuevoVehiculo.cliente_id || '');

      if (modoEdicion) {
        await api.put(`${process.env.REACT_APP_API_URL}/api/vehiculos/${vehiculoActual.placa}/`, formData);
      } else {
        await api.post(`${process.env.REACT_APP_API_URL}/api/vehiculos/`, formData);
      }

      fetchVehiculos();
      cerrarModal();
    } catch (err) {
      console.error('Error al guardar vehículo', err);
      setErrorMensaje('Ocurrió un error al guardar el vehículo. Verifica que la placa no esté repetida.');
    }
  };

  const confirmarEliminacion = (vehiculo) => {
    setVehiculoAEliminar(vehiculo);
    setDialogoAbierto(true);
  };

  const verTarjeta = async (urlTarjeta) => {
    try {
      const blobUrl = await obtenerImagenAutenticada(urlTarjeta, api);
      setImagenSeleccionada(blobUrl);
    } catch (err) {
      console.error('Error al cargar la tarjeta de propiedad', err);
      alert('No se pudo cargar la tarjeta de propiedad. Verifique su sesión.');
    }
  };

  const cerrarImagen = () => {
    if (imagenSeleccionada) URL.revokeObjectURL(imagenSeleccionada);
    setImagenSeleccionada(null);
  };

  const abrirHistorial = async (vehiculo) => {
    setVehiculoHistorial(vehiculo);
    setHistorialAbierto(true);
    setCargandoHistorial(true);
    try {
      const response = await api.get(`${process.env.REACT_APP_API_URL}/api/reparaciones/?vehiculo=${vehiculo.placa}`);
      setReparacionesHistorial(response.data);
    } catch (err) {
      console.error('Error al cargar historial de reparaciones', err);
      setReparacionesHistorial([]);
    } finally {
      setCargandoHistorial(false);
    }
  };

  const cerrarHistorial = () => {
    setHistorialAbierto(false);
    setVehiculoHistorial(null);
    setReparacionesHistorial([]);
  };

  const eliminarVehiculoConfirmado = async () => {
    try {
      await api.delete(`${process.env.REACT_APP_API_URL}/api/vehiculos/${vehiculoAEliminar.placa}/`);
      fetchVehiculos();
      setDialogoAbierto(false);
      setVehiculoAEliminar(null);
    } catch (err) {
      console.error('Error al eliminar vehículo', err);
    }
  };

  return (
    <Paper sx={{ padding: 2 }}>
      {/* Encabezado */}
        <motion.div
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.6, 0.01, 0.05, 0.95] }}
        >
          <Typography className="titulo-app" variant="h5" gutterBottom>
            <DirectionsCarIcon sx={{ mr: 1, fontSize: 32, color: '#083a94', verticalAlign: 'middle' }} />
            Vehículos Registrados
          </Typography>
          <motion.div
            whileHover={{ scale: 1.07, boxShadow: "0 4px 20px #1976d244" }}
            transition={{ type: "spring", stiffness: 400, damping: 15 }}
            style={{ display: 'inline-block' }}
          >
            <Button
              variant="contained"
              className="btn-app"
              startIcon={<Add />}
              onClick={() => abrirModal()}
            >
              Agregar Vehículo
            </Button>
          </motion.div>
        </motion.div>

      <TextField
        label="Buscar vehículo"
        placeholder="Placa, marca, línea, modelo, color o cliente..."
        size="small"
        fullWidth
        value={busqueda}
        onChange={(e) => setBusqueda(e.target.value)}
        sx={{ mb: 2 }}
      />

      {/* Tabla */}
      {isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {vehiculosFiltrados.map((v) => (
            <Card key={v.placa} variant="outlined" sx={{ p: 2 }}>
              <Typography sx={{ fontWeight: 700 }}>{v.placa}</Typography>
              <Typography variant="body2" color="text.secondary">{v.marca} {v.linea} · {v.modelo} · {v.color}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Cliente: {v.cliente ? v.cliente.nombre : '—'}
              </Typography>
              {v.tarjeta_propiedad && (
                <Button size="small" variant="outlined" onClick={() => verTarjeta(v.tarjeta_propiedad)} sx={{ mb: 1 }}>
                  Ver tarjeta
                </Button>
              )}
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Tooltip title="Ver historial de reparaciones" arrow>
                  <IconButton onClick={() => abrirHistorial(v)}>
                    <HistoryIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Editar" arrow>
                  <IconButton onClick={() => abrirModal(v)}>
                    <Edit />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Eliminar" arrow>
                  <IconButton color="error" onClick={() => confirmarEliminacion(v)}>
                    <Delete />
                  </IconButton>
                </Tooltip>
              </Box>
            </Card>
          ))}
        </Box>
      ) : (
      <TableContainer sx={{ overflowX: 'auto' }}>
      <Table className="tabla-app">
        <TableHead>
          <TableRow>
            <TableCell>Placa</TableCell>
            <TableCell>Marca</TableCell>
            <TableCell>Línea</TableCell>
            <TableCell>Modelo</TableCell>
            <TableCell>Color</TableCell>
            <TableCell>Cliente</TableCell>
            <TableCell>Tarjeta</TableCell>
            <TableCell>Acciones</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {vehiculosFiltrados.map((v, idx) => (
                <motion.tr
                  key={v.placa}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.06 * idx }}
                  whileHover={{ scale: 1.01, boxShadow: "0 2px 16px #083a9422" }}
                  style={{ cursor: 'pointer' }}
                >
              <TableCell>{v.placa}</TableCell>
              <TableCell>{v.marca}</TableCell>
              <TableCell>{v.linea}</TableCell>
              <TableCell>{v.modelo}</TableCell>
              <TableCell>{v.color}</TableCell>
              <TableCell>{v.cliente ? v.cliente.nombre : '—'}</TableCell>
              <TableCell>
                {v.tarjeta_propiedad ? (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => verTarjeta(v.tarjeta_propiedad)}
                  >
                    Ver
                  </Button>
                ) : '—'}
              </TableCell>
              <TableCell>
                <Tooltip title="Ver historial de reparaciones" arrow>
                  <IconButton onClick={() => abrirHistorial(v)}>
                    <HistoryIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Editar" arrow>
                  <IconButton onClick={() => abrirModal(v)}>
                    <Edit />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Eliminar" arrow>
                  <IconButton color="error" onClick={() => confirmarEliminacion(v)}>
                    <Delete />
                  </IconButton>
                </Tooltip>
              </TableCell>
              </motion.tr>

          ))}
        </TableBody>
      </Table>
      </TableContainer>
      )}

      {/* Modal crear/editar */}
      <Modal open={modalOpen} onClose={cerrarModal}>
        <Box className="modal-app" sx={{ ...modalStyle, position: 'relative' }}>
          {/* Botón de cerrar */}
          <IconButton
            aria-label="Cerrar"
            onClick={cerrarModal}
            sx={{ position: 'absolute', right: 16, top: 16, color: '#888' }}
          >
            <Close />
          </IconButton>
          <Box display="flex" alignItems="center" mb={2}>
            {modoEdicion
              ? <Edit sx={{ fontSize: 32, color: '#0a5cd6', mr: 1 }} />
              : <DirectionsCarIcon sx={{ fontSize: 32, color: '#0a5cd6', mr: 1 }} />
            }
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {modoEdicion ? 'Editar Vehículo' : 'Registrar Vehículo'}
            </Typography>
          </Box>
          {errorMensaje && <Alert severity="error">{errorMensaje}</Alert>}
          <form autoComplete="off" onSubmit={handleSubmit}>
            <TextField
              label="Placa"
              name="placa"
              fullWidth
              margin="normal"
              value={nuevoVehiculo.placa}
              onChange={handleChange}
              disabled={modoEdicion}
              error={!!errores.placa}
              helperText={errores.placa}
              autoFocus
            />
            <TextField label="Marca" name="marca" fullWidth margin="dense" value={nuevoVehiculo.marca} onChange={handleChange} error={!!errores.marca} helperText={errores.marca} />
            <TextField label="Línea" name="linea" fullWidth margin="dense" value={nuevoVehiculo.linea} onChange={handleChange} error={!!errores.linea} helperText={errores.linea} />
            <TextField label="Modelo" name="modelo" fullWidth margin="dense" value={nuevoVehiculo.modelo} onChange={handleChange} error={!!errores.modelo} helperText={errores.modelo} />
            <TextField label="Color" name="color" fullWidth margin="dense" value={nuevoVehiculo.color} onChange={handleChange} error={!!errores.color} helperText={errores.color} />
            <TextField
              select
              label="Cliente (opcional)"
              name="cliente_id"
              fullWidth
              margin="dense"
              value={nuevoVehiculo.cliente_id || ''}
              onChange={handleChange}
            >
              <MenuItem value="">Sin asignar</MenuItem>
              {clientes.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.nombre} — {c.cedula}</MenuItem>
              ))}
            </TextField>
            <input
              type="file"
              name="tarjeta_propiedad"
              onChange={handleChange}
              accept="image/*"
              style={{ marginTop: '10px' }}
            />
            <Box display="flex" justifyContent="flex-end" mt={2}>
              <Button onClick={cerrarModal} sx={{ mr: 1 }}>Cancelar</Button>
              <Button type="submit" variant="contained">
                {modoEdicion ? 'Actualizar' : 'Guardar'}
              </Button>
            </Box>
          </form>
        </Box>
      </Modal>

      {/* Modal ver tarjeta */}
      <Modal open={!!imagenSeleccionada} onClose={cerrarImagen}>
        <Box
          className="modal-app"
          sx={{
            ...modalStyle,
            maxWidth: '90%',
            maxHeight: '90%',
            overflow: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <img src={imagenSeleccionada} alt="Tarjeta de propiedad" style={{ width: '100%', height: 'auto', borderRadius: 8 }} />
        </Box>
      </Modal>

      {/* Diálogo eliminar */}
      <Dialog open={dialogoAbierto} onClose={() => setDialogoAbierto(false)}>
        <DialogTitle className="dialog-title-alert">
          <WarningAmberIcon sx={{ fontSize: 28 }} />
          ¿Eliminar vehículo?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Estás seguro que deseas eliminar el vehículo con placa <strong>{vehiculoAEliminar?.placa}</strong>? Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions className="dialog-actions-custom">
          <Button onClick={() => setDialogoAbierto(false)} className="dialog-btn-cancel">
            Cancelar
          </Button>
          <Button onClick={eliminarVehiculoConfirmado} variant="contained" className="dialog-btn-delete">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo historial de reparaciones */}
      <Dialog open={historialAbierto} onClose={cerrarHistorial} maxWidth="md" fullWidth>
        <DialogTitle>
          Historial de reparaciones — {vehiculoHistorial?.placa}
        </DialogTitle>
        <DialogContent>
          {cargandoHistorial ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : reparacionesHistorial.length === 0 ? (
            <DialogContentText>Este vehículo no tiene reparaciones registradas.</DialogContentText>
          ) : isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {reparacionesHistorial.map((r) => (
                <Card key={r.id} variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">{r.fecha} · Orden #{r.orden}</Typography>
                  <Typography sx={{ fontWeight: 600 }}>
                    {r.garantia ? `[Garantía] ${r.descripcion}` : r.descripcion}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Técnico: {r.tecnico?.nombre || '—'}</Typography>
                  <Typography sx={{ fontWeight: 700, mt: 0.5 }}>{formatearPrecio(r.costo)}</Typography>
                </Card>
              ))}
            </Box>
          ) : (
            <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Orden</TableCell>
                  <TableCell>Descripción</TableCell>
                  <TableCell>Técnico</TableCell>
                  <TableCell align="right">Valor</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reparacionesHistorial.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>{r.fecha}</TableCell>
                    <TableCell>#{r.orden}</TableCell>
                    <TableCell>{r.garantia ? `[Garantía] ${r.descripcion}` : r.descripcion}</TableCell>
                    <TableCell>{r.tecnico?.nombre || '—'}</TableCell>
                    <TableCell align="right">{formatearPrecio(r.costo)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={cerrarHistorial}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default Vehiculos;
